import { Button, Form, Input, Select, TreeSelect, notification, Switch } from "antd";
import { Rule } from "antd/es/form";
import { useForm } from "antd/es/form/Form";
import { FormInstance, FormProps } from "antd/lib";
import { MutableRefObject, ReactElement, useImperativeHandle, useState } from "react";
import { useTranslation } from 'react-i18next';

type FormItemType = "input" | "textarea" | "select" | 'treeSelect' | 'switch' | 'avatar';

interface ModalFormRef {  
  modalForm: FormInstance;  
} 

interface FormItem<T> {
  label: string;
  key: keyof T;
  type: FormItemType;
  value: unknown;
  rules?: Array<Rule>;
  maxLength?: number;
  otherProps?: {
    [props in string]: unknown;
  };
}

export interface ICustomForm<T, Q> {
  isFormItemOperate?: boolean, // 是否是表单操作项
  formRef?: MutableRefObject<ModalFormRef | null>;
  formName: string;
  formItems: Array<FormItem<T>>;
  handleCancel: () => void;
  handleOk: (res: Q) => void;
  okApi: (info: T) => Promise<Q>;
}

function CustomForm<T, Q>({
  isFormItemOperate = true, // 是否是表单操作项
  formRef,
  formName,
  formItems,
  handleCancel,
  handleOk,
  okApi,
}: ICustomForm<T, Q>): ReactElement {
  const { t } = useTranslation();
  const [modalForm] = useForm()
  const [notificationApi, contextHolder] = notification.useNotification({ placement: 'top' });
  const [loading, setLoading] = useState<boolean>(false);

  useImperativeHandle(formRef, () => ({
    modalForm
  }), [modalForm])

  const onFinish = () => {
    setLoading(true)

    modalForm.validateFields()
      .then(res => {
        okApi(res)
          .then((info) => {
            handleOk(info);
            handleCancel()
          })
      })
      .catch(error => {
        notificationApi.error({ message: t('layout.validaFailed'), description: error.errorFields[0].errors[0] })
      })
      .finally(() => {
        setTimeout(() => {
          setLoading(false)
        }, 500);
      })
  }

  const onFinishFailed: FormProps<T>['onFinishFailed'] = (errorInfo) => {
    notificationApi.error({ message: t('layout.validaFailed'), description: errorInfo?.errorFields[0]?.errors[0] })
  }

  const maxLengthValidator = (rule: Rule, value: string, callback: (error?: string) => void) => {
    if (value && value.length > (rule as { max: number }).max) {
      callback(t('member.maxLengthTip'));  
    } else {  
      callback();  
    }  
  };

  return (
    <Form style={{ marginTop: 16 }} name={formName} onFinish={onFinish} onFinishFailed={onFinishFailed} autoComplete="off" form={modalForm} scrollToFirstError labelAlign='right'>
      {contextHolder}
      {formItems?.map(({ label, key, value, type, rules = [], maxLength, otherProps }) => (
        <Form.Item
          label={label}
          key={label}
          name={String(key)}
          initialValue={value}
          validateDebounce={1000}
          rules={[
            ...rules,
            ...(maxLength ? [{ validator: maxLengthValidator, max: maxLength, message: t('member.maxLengthTip') }] : [])
          ]}
        >
          {type === 'input' && <Input {...otherProps} onChange={e => {
            if(formName === 'categoryAdd' && key === 'code') {
              modalForm.setFieldsValue({ code: e.target.value.replace(/\D/g, '') })
            }
          } }/>}
          {type === 'select' && <Select {...otherProps} />}
          {type === 'textarea' && <Input.TextArea  {...otherProps} />}
          {type === 'treeSelect' && <TreeSelect {...otherProps} />}
          {type === 'switch' && <Switch {...otherProps} />}
        </Form.Item>
      ))}
      {isFormItemOperate && (
        <Form.Item style={{ display: 'flex', justifyContent: 'end' }}>
          <Button type="link" htmlType='reset' onClick={handleCancel}>
            {t('layout.cancel')}          </Button>
          <Button type="primary" loading={loading} htmlType="submit">
            {t('layout.confirm')}
          </Button>
        </Form.Item>
      )}
    </Form>
  );
}

export default CustomForm;
